// v6 F14 §4 — Doorzoekbare landkeuze via Popover + Command (cmdk).
// Toont "Veelgebruikt" (BE/NL/FR/DE/LU) bovenaan, daarna alfabetisch.
// Value = ISO-3166-1 alpha-2 code (bv. "BE"). onChange geeft de nieuwe code.

import React, { useState, useMemo } from "react";
import { Check, ChevronsUpDown, Globe } from "lucide-react";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { COUNTRIES, COMMON_CODES, countryLabel } from "../lib/countries";

export default function CountrySelect({
  value,
  onChange,
  placeholder = "Kies een land...",
  testId = "country-select",
  className = "",
}) {
  const [open, setOpen] = useState(false);

  const { commonList, restList } = useMemo(() => {
    const commonSet = new Set(COMMON_CODES);
    const common = COMMON_CODES
      .map((c) => COUNTRIES.find((x) => x.code === c))
      .filter(Boolean);
    const rest = [...COUNTRIES]
      .filter((c) => !commonSet.has(c.code))
      .sort((a, b) => a.label.localeCompare(b.label, "nl", { sensitivity: "base" }));
    return { commonList: common, restList: rest };
  }, []);

  const selectedLabel = value ? countryLabel(value) : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`h-11 rounded-xl w-full justify-between font-normal ${className}`}
          data-testid={testId}
        >
          <span className="flex items-center gap-2 min-w-0">
            <Globe className="w-4 h-4 text-[#5D7259] flex-shrink-0" />
            {selectedLabel ? (
              <span className="truncate">{selectedLabel}</span>
            ) : (
              <span className="truncate text-[#A8A7A0]">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="w-4 h-4 text-[#A8A7A0] flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] min-w-[240px] p-0"
        align="start"
        data-testid={`${testId}-popover`}
      >
        <Command>
          <CommandInput
            placeholder="Zoek land..."
            data-testid={`${testId}-input`}
          />
          <CommandList className="max-h-72">
            <CommandEmpty>Geen land gevonden.</CommandEmpty>
            <CommandGroup heading="Veelgebruikt">
              {commonList.map((c) => (
                <CommandItem
                  key={c.code}
                  value={`${c.label} ${c.code}`}
                  onSelect={() => {
                    onChange(c.code);
                    setOpen(false);
                  }}
                  data-testid={`${testId}-option-${c.code}`}
                >
                  <Check className={`w-4 h-4 mr-2 ${value === c.code ? "opacity-100" : "opacity-0"}`} />
                  {c.label}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Andere landen">
              {restList.map((c) => (
                <CommandItem
                  key={c.code}
                  value={`${c.label} ${c.code}`}
                  onSelect={() => {
                    onChange(c.code);
                    setOpen(false);
                  }}
                  data-testid={`${testId}-option-${c.code}`}
                >
                  <Check className={`w-4 h-4 mr-2 ${value === c.code ? "opacity-100" : "opacity-0"}`} />
                  {c.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
